// src/components/admin/AdminAccessTiersPanel.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { useTranslation } from "../../i18n";
import api from "../../api/api";
import { athenaColors } from "../../theme/palette";
import AdminBetaUsersPanel from "../billing/AdminBetaUsersPanel";
import { glass } from "../../theme/glassTokens";
import { athenaDarkPaperSx } from "../layout/AthenaDialogStyles";

type AccessTier = "waitlisted" | "active" | "beta";
type FilterKey = "all" | "founders" | "beta" | "active" | "waitlisted" | "expired";

interface AccessUser {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  access_tier: AccessTier;
  is_beta_user: boolean;
  is_system_admin: boolean;
  is_environment_admin: boolean;
  beta_requested: boolean;
  beta_requested_at: string | null;
  subscription_tier: string | null;
  beta_expires_at: string | null;
  custom_monthly_token_limit: number | null;
  is_expired_beta: boolean;
}

const TIER_LABELS: Record<AccessTier, string> = {
  waitlisted: "Waitlisted",
  active: "Active",
  beta: "Beta Tester",
};

const TIER_COLORS: Record<AccessTier, string> = {
  waitlisted: athenaColors.blueGray || "#78909C",
  active: "#4caf50",
  beta: glass.accent,
};

const DURATION_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
  { value: 180, label: "180 days" },
];

const AdminAccessTiersPanel: React.FC = () => {
  const theme = useTheme();
  const textPrimary =
    glass.textPrimary;
  const textSecondary =
    glass.textSecondary;
  const { t } = useTranslation();

  const [users, setUsers] = useState<AccessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [filter, setFilter] = useState<FilterKey>("all");

  // Grant Beta dialog
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [grantUserId, setGrantUserId] = useState<number | null>(null);
  const [grantDuration, setGrantDuration] = useState(90);
  const [grantCustomDuration, setGrantCustomDuration] = useState("");
  const [grantTokenLimit, setGrantTokenLimit] = useState("");
  const [grantSubmitting, setGrantSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/billing/admin/access-tiers/");
      setUsers(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleTierChange = async (userId: number, newTier: AccessTier) => {
    setUpdatingId(userId);
    setError(null);
    try {
      await api.patch(`/billing/admin/access-tiers/${userId}/`, {
        access_tier: newTier,
      });
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update tier.");
    } finally {
      setUpdatingId(null);
    }
  };

  // Joan, 2026-05-27: toggle the gatekeeper flag (lets the user access
  // /env-memberships even when they are not a system admin).
  const handleEnvAdminToggle = async (
    userId: number,
    nextValue: boolean,
  ) => {
    setUpdatingId(userId);
    setError(null);
    try {
      await api.patch(`/billing/admin/access-tiers/${userId}/`, {
        is_environment_admin: nextValue,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, is_environment_admin: nextValue } : u,
        ),
      );
    } catch (err: any) {
      setError(
        err.response?.data?.detail || "Failed to update env-admin flag.",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const openGrantDialog = (userId: number) => {
    setGrantUserId(userId);
    setGrantDuration(90);
    setGrantCustomDuration("");
    setGrantTokenLimit("");
    setGrantDialogOpen(true);
  };

  const handleGrantBeta = async () => {
    if (!grantUserId) return;
    setGrantSubmitting(true);
    setError(null);
    try {
      const durationDays = grantCustomDuration
        ? parseInt(grantCustomDuration, 10)
        : grantDuration;
      await api.post("/billing/admin/grant-beta/", {
        user_id: grantUserId,
        duration_days: durationDays,
        custom_token_limit: grantTokenLimit
          ? parseInt(grantTokenLimit, 10)
          : null,
      });
      setGrantDialogOpen(false);
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to grant beta access.");
    } finally {
      setGrantSubmitting(false);
    }
  };

  const getDisplayName = (u: AccessUser) => {
    const full = `${u.first_name || ""} ${u.last_name || ""}`.trim();
    return full || u.username || u.email;
  };

  const getInitials = (u: AccessUser) =>
    getDisplayName(u)
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const filteredUsers = users.filter((u) => {
    switch (filter) {
      case "founders":
        return u.is_beta_user;
      case "beta":
        return u.access_tier === "beta" && !u.is_beta_user;
      case "active":
        return u.access_tier === "active";
      case "waitlisted":
        return u.access_tier === "waitlisted";
      case "expired":
        return u.is_expired_beta;
      default:
        return true;
    }
  });

  const counts: Record<FilterKey, number> = {
    all: users.length,
    founders: users.filter((u) => u.is_beta_user).length,
    beta: users.filter((u) => u.access_tier === "beta" && !u.is_beta_user).length,
    active: users.filter((u) => u.access_tier === "active").length,
    waitlisted: users.filter((u) => u.access_tier === "waitlisted").length,
    expired: users.filter((u) => u.is_expired_beta).length,
  };

  const FILTER_COLORS: Record<FilterKey, string> = {
    all: glass.accent,
    founders: "#ff9800",
    beta: glass.accent,
    active: "#4caf50",
    waitlisted: athenaColors.blueGray || "#78909C",
    expired: "#f44336",
  };

  const FILTER_LABELS: Record<FilterKey, string> = {
    all: "All",
    founders: "Founders",
    beta: "Beta",
    active: "Active",
    waitlisted: "Waitlisted",
    expired: "Expired",
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUserRole = (u: AccessUser): string => {
    if (u.is_beta_user) return "Founder";
    if (u.access_tier === "beta") return "Beta";
    return u.access_tier.charAt(0).toUpperCase() + u.access_tier.slice(1);
  };

  const getRoleColor = (u: AccessUser): string => {
    if (u.is_beta_user) return "#ff9800";
    return TIER_COLORS[u.access_tier] || glass.accent;
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${athenaColors.deepNavy}, ${athenaColors.royalBlue})`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <GroupIcon
              sx={{ color: theme.palette.common.white, fontSize: 22 }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: textPrimary }}
            >
              {t("admin.access_tiers.title", "Platform Access Management")}
            </Typography>
            <Typography variant="caption" sx={{ color: textSecondary }}>
              {t(
                "admin.access_tiers.subtitle",
                "Control who can enter the platform and at what level."
              )}
            </Typography>
          </Box>
        </Box>

        {/* Filter chips */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {(Object.keys(FILTER_LABELS) as FilterKey[]).map((key) => (
            <Chip
              key={key}
              label={`${FILTER_LABELS[key]} (${counts[key]})`}
              size="small"
              variant={filter === key ? "filled" : "outlined"}
              onClick={() => setFilter(key)}
              sx={{
                fontWeight: 600,
                fontSize: "0.7rem",
                cursor: "pointer",
                ...(filter === key
                  ? {
                      bgcolor: alpha(FILTER_COLORS[key], 0.15),
                      color: FILTER_COLORS[key],
                      borderColor: FILTER_COLORS[key],
                    }
                  : {}),
              }}
            />
          ))}
        </Box>
      </Box>

      {error && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.light, 0.08),
            border: `1px solid ${alpha(theme.palette.error.light, 0.2)}`,
          }}
        >
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Box>
      )}

      {/* User list */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", py: 3, color: textSecondary }}
          >
            No users found.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {filteredUsers.map((user) => (
              <Box
                key={user.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: glass.rowDivider,
                  border: `1px solid ${glass.rowDivider}`,
                  transition: "background-color 0.15s",
                  "&:hover": {
                    bgcolor: glass.rowDivider,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.8rem",
                    bgcolor: alpha(getRoleColor(user), 0.15),
                    color: getRoleColor(user),
                  }}
                >
                  {getInitials(user)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {getDisplayName(user)}
                    </Typography>
                    <Chip
                      label={getUserRole(user)}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        bgcolor: alpha(getRoleColor(user), 0.12),
                        color: getRoleColor(user),
                      }}
                    />
                    {user.is_system_admin && (
                      <Chip
                        label="Admin"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.warning.main, 0.12),
                          color: theme.palette.warning.main,
                        }}
                      />
                    )}
                    {/* Joan, 2026-05-27: env-admin (gatekeeper) toggle. Click
                        flips it; chip is filled when on, outlined when off. */}
                    <Tooltip
                      title={
                        user.is_environment_admin
                          ? t(
                              "admin.access_tiers.env_admin_on_tip",
                              "Env admin — has access to bulk env memberships. Click to revoke.",
                            )
                          : t(
                              "admin.access_tiers.env_admin_off_tip",
                              "Promote to env admin (gatekeeper of all env memberships).",
                            )
                      }
                      arrow
                    >
                      <Chip
                        label={
                          user.is_environment_admin ? "Env admin" : "+ Env admin"
                        }
                        size="small"
                        clickable
                        onClick={() =>
                          handleEnvAdminToggle(
                            user.id,
                            !user.is_environment_admin,
                          )
                        }
                        disabled={updatingId === user.id}
                        variant={
                          user.is_environment_admin ? "filled" : "outlined"
                        }
                        sx={{
                          height: 18,
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          ...(user.is_environment_admin
                            ? {
                                bgcolor: alpha(
                                  glass.accent,
                                  0.18,
                                ),
                                color: glass.accent,
                                borderColor: "transparent",
                              }
                            : {
                                borderColor: alpha(
                                  glass.accent,
                                  0.4,
                                ),
                                color: glass.accent,
                              }),
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: textSecondary }}
                      noWrap
                    >
                      {user.email}
                    </Typography>
                    {user.subscription_tier && (
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary, fontStyle: "italic" }}
                      >
                        {user.subscription_tier}
                      </Typography>
                    )}
                    {user.beta_expires_at && (
                      <Tooltip title={`Beta expires: ${formatDate(user.beta_expires_at)}`}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: user.is_expired_beta
                              ? theme.palette.error.light
                              : textSecondary,
                            fontWeight: user.is_expired_beta ? 700 : 400,
                          }}
                        >
                          {user.is_expired_beta
                            ? "Expired"
                            : `Exp: ${formatDate(user.beta_expires_at)}`}
                        </Typography>
                      </Tooltip>
                    )}
                    {user.custom_monthly_token_limit != null && (
                      <Typography
                        variant="caption"
                        sx={{ color: textSecondary }}
                      >
                        {(user.custom_monthly_token_limit / 1_000_000).toFixed(1)}M tok
                      </Typography>
                    )}
                    {user.beta_requested && (
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 14, color: "#4caf50" }}
                        titleAccess={`Beta requested${user.beta_requested_at ? ` on ${new Date(user.beta_requested_at).toLocaleDateString()}` : ""}`}
                      />
                    )}
                  </Box>
                </Box>

                {/* Grant Beta button */}
                {!user.is_beta_user && user.access_tier !== "beta" && (
                  <Tooltip title="Grant time-limited beta access">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CardGiftcardIcon sx={{ fontSize: 16 }} />}
                      onClick={() => openGrantDialog(user.id)}
                      sx={{
                        fontSize: "0.7rem",
                        textTransform: "none",
                        minWidth: "auto",
                        py: 0.25,
                        px: 1,
                      }}
                    >
                      Grant Beta
                    </Button>
                  </Tooltip>
                )}

                {/* Tier selector */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select
                    value={user.access_tier}
                    onChange={(e: SelectChangeEvent) =>
                      handleTierChange(user.id, e.target.value as AccessTier)
                    }
                    disabled={updatingId === user.id}
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      height: 32,
                      "& .MuiSelect-select": {
                        py: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      },
                    }}
                  >
                    {(Object.keys(TIER_LABELS) as AccessTier[]).map((tier) => (
                      <MenuItem key={tier} value={tier} sx={{ fontSize: "0.8rem" }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: TIER_COLORS[tier],
                            mr: 1,
                            display: "inline-block",
                          }}
                        />
                        {TIER_LABELS[tier]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {updatingId === user.id && <CircularProgress size={16} />}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Founders Panel (existing) */}
      <AdminBetaUsersPanel />

      {/* Grant Beta Dialog */}
      {/* Dialogs render in a PORTAL, so VideoGlassContainer's darkOverrides never
          reach them. Without the shared dark paper this title would inherit
          text.primary (deepNavy) while the body below uses the white glass tokens —
          mixed ink on one surface. */}
      <Dialog
        open={grantDialogOpen}
        onClose={() => setGrantDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: glass.textPrimary }}>
          Grant Beta Access
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>
            This will set the user's access tier to &quot;beta&quot; and assign the Beta Tester
            subscription tier with time-limited access.
          </Typography>
          <FormControl fullWidth size="small">
            <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
              Duration
            </Typography>
            <Select
              value={grantCustomDuration ? "custom" : String(grantDuration)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  setGrantCustomDuration("90");
                } else {
                  setGrantCustomDuration("");
                  setGrantDuration(parseInt(e.target.value, 10));
                }
              }}
            >
              {DURATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom...</MenuItem>
            </Select>
          </FormControl>
          {grantCustomDuration && (
            <TextField
              size="small"
              label="Custom days"
              type="number"
              value={grantCustomDuration}
              onChange={(e) => setGrantCustomDuration(e.target.value)}
              inputProps={{ min: 1 }}
            />
          )}
          <TextField
            size="small"
            label="Custom token limit (optional)"
            type="number"
            value={grantTokenLimit}
            onChange={(e) => setGrantTokenLimit(e.target.value)}
            helperText="Leave empty to use the beta tier default (6.5M tokens/month)"
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantDialogOpen(false)} disabled={grantSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGrantBeta}
            disabled={grantSubmitting}
            startIcon={grantSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            Grant Beta
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAccessTiersPanel;
